import { useEffect, useState } from "react";
import { Loader2, CalendarDays, Users, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LiveAvailability, useGuestyListings } from "@/components/LiveAvailability";
import { useCart } from "@/context/CartContext";
import { useExperiences } from "@/hooks/useExperiences";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

const AddonPicker = ({ experiences, selectedIds, onToggle }) => (
  <div className="space-y-2" data-testid="addon-picker">
    <p className="font-body text-[11px] uppercase tracking-wider text-muted-foreground pt-1">
      Enhance Your Stay (optional)
    </p>
    {experiences.map((exp) => {
      const checked = selectedIds.includes(exp.id);
      return (
        <label
          key={exp.id}
          data-testid={`addon-${exp.id}`}
          className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
            checked ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"
          }`}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(exp.id)}
            className="accent-[var(--primary)] h-4 w-4 shrink-0"
            data-testid={`addon-checkbox-${exp.id}`}
          />
          <span className="font-body text-sm text-foreground flex-1">{exp.name}</span>
          <span className="font-body text-sm font-semibold text-primary">${exp.price}</span>
        </label>
      );
    })}
  </div>
);

const QuoteSummary = ({ quote, selectedAddons, grandTotal }) => (
  <div data-testid="booking-quote" className="rounded-lg bg-muted/50 p-4 space-y-2">
    <div className="flex justify-between font-body text-sm text-foreground/80">
      <span>{quote.nights} night{quote.nights > 1 ? "s" : ""} stay</span>
      <span data-testid="booking-stay-subtotal">${quote.total.toFixed(2)}</span>
    </div>
    {selectedAddons.map((a) => (
      <div key={a.id} className="flex justify-between font-body text-sm text-foreground/80" data-testid={`quote-addon-${a.id}`}>
        <span>+ {a.name}</span>
        <span>${a.price.toFixed(2)}</span>
      </div>
    ))}
    <div className="flex justify-between font-heading text-lg font-semibold text-foreground border-t border-border/60 pt-2">
      <span>Total</span>
      <span data-testid="booking-quote-total">${grandTotal.toFixed(2)} {quote.currency}</span>
    </div>
  </div>
);

const GuestForm = ({ form, setForm, maxGuests }) => {
  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input data-testid="booking-first-name" placeholder="First name" value={form.first_name} onChange={update("first_name")} className="font-body" />
        <Input data-testid="booking-last-name" placeholder="Last name" value={form.last_name} onChange={update("last_name")} className="font-body" />
      </div>
      <Input data-testid="booking-email" type="email" placeholder="Email" value={form.email} onChange={update("email")} className="font-body" />
      <Input data-testid="booking-phone" type="tel" placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} className="font-body" />
      <div className="flex items-center gap-2">
        <Users size={16} className="text-muted-foreground shrink-0" />
        <select
          data-testid="booking-guests-select"
          value={form.guests_count}
          onChange={update("guests_count")}
          className="w-full h-10 rounded-md border border-input bg-background px-3 font-body text-sm"
        >
          {Array.from({ length: maxGuests }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n} guest{n > 1 ? "s" : ""}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const ListingPicker = ({ listings, selected, onSelect }) => (
  <div className="flex flex-wrap gap-2 mb-5" data-testid="listing-picker">
    {listings.map((l) => (
      <button
        key={l.id}
        data-testid={`listing-option-${l.id}`}
        onClick={() => onSelect(l.id)}
        className={`px-4 py-2 rounded-full font-body text-sm border transition-colors ${
          selected?.id === l.id
            ? "bg-primary text-white border-primary"
            : "bg-white border-border text-foreground/70 hover:border-primary/50 hover:text-primary"
        }`}
      >
        {l.title || l.nickname}
        {l.base_price != null && (
          <span className={`ml-2 text-xs ${selected?.id === l.id ? "text-white/80" : "text-muted-foreground"}`}>
            from ${l.base_price}/nt
          </span>
        )}
      </button>
    ))}
  </div>
);

const StayPanel = ({
  listing, checkIn, checkOut, quoting, quoteError, quote,
  selectedAddons, grandTotal, experiences, addonIds, toggleAddon,
  form, setForm, submitError, submitting, formValid, onSubmit, onAddToCart,
}) => (
  <div className="border border-border/60 rounded-xl p-5 md:p-6 bg-white space-y-4 lg:sticky lg:top-24">
    <h3 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
      <CalendarDays size={18} className="text-primary" />
      Your Stay
    </h3>
    <p className="font-body text-xs text-muted-foreground -mt-2" data-testid="booking-selected-listing">
      {listing.title || listing.nickname}
    </p>

    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-border/60 p-3">
        <p className="font-body text-[11px] uppercase tracking-wider text-muted-foreground">Check-in</p>
        <p data-testid="booking-checkin-display" className="font-body text-sm font-medium text-foreground mt-1">
          {fmtDate(checkIn)}
        </p>
      </div>
      <div className="rounded-lg border border-border/60 p-3">
        <p className="font-body text-[11px] uppercase tracking-wider text-muted-foreground">Check-out</p>
        <p data-testid="booking-checkout-display" className="font-body text-sm font-medium text-foreground mt-1">
          {fmtDate(checkOut)}
        </p>
      </div>
    </div>

    {!checkIn && (
      <p className="font-body text-sm text-muted-foreground">
        Select your check-in date on the calendar to get started.
      </p>
    )}
    {checkIn && !checkOut && (
      <p className="font-body text-sm text-muted-foreground">
        Now select your check-out date.
      </p>
    )}

    {quoting && (
      <div className="flex items-center gap-2 font-body text-sm text-muted-foreground" data-testid="booking-quoting">
        <Loader2 size={14} className="animate-spin" /> Checking rates...
      </div>
    )}
    {quoteError && (
      <p data-testid="booking-quote-error" className="font-body text-sm text-destructive">
        {quoteError}
      </p>
    )}
    {quote && <QuoteSummary quote={quote} selectedAddons={selectedAddons} grandTotal={grandTotal} />}

    {quote && (
      <>
        {experiences.length > 0 && (
          <AddonPicker experiences={experiences} selectedIds={addonIds} onToggle={toggleAddon} />
        )}
        <GuestForm form={form} setForm={setForm} maxGuests={listing?.accommodates || 6} />
        {submitError && (
          <p data-testid="booking-submit-error" className="font-body text-sm text-destructive">
            {submitError}
          </p>
        )}
        <Button
          data-testid="booking-pay-button"
          onClick={onSubmit}
          disabled={!formValid || submitting}
          className="w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full h-11"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Redirecting to payment...
            </span>
          ) : (
            `Pay $${grandTotal.toFixed(2)} & Book`
          )}
        </Button>
        <Button
          data-testid="booking-add-to-cart"
          onClick={onAddToCart}
          variant="outline"
          className="w-full font-body rounded-full h-11 border-primary text-primary hover:bg-primary hover:text-white"
        >
          <ShoppingCart size={16} className="mr-2" /> Add to Cart & Keep Browsing
        </Button>
        <p className="font-body text-[11px] text-muted-foreground text-center">
          Secure payment via Stripe. Your reservation is confirmed instantly.
        </p>
      </>
    )}
  </div>
);

export const BookingFlow = ({ listingFilter = "cabin" }) => {
  const { listings, loading: listingsLoading, error: listingsError } = useGuestyListings(listingFilter);
  const experiences = useExperiences();
  const [selectedId, setSelectedId] = useState(null);
  const listing = listings.find((l) => l.id === selectedId) || listings[0] || null;

  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [addonIds, setAddonIds] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", guests_count: 2 });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const { addStay, addExperience } = useCart();

  const selectedAddons = experiences.filter((e) => addonIds.includes(e.id));
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
  const grandTotal = (quote?.total || 0) + addonsTotal;

  const toggleAddon = (id) =>
    setAddonIds((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const resetSelection = () => {
    setCheckIn(null);
    setCheckOut(null);
    setQuote(null);
    setQuoteError(null);
    setSubmitError(null);
  };

  const handleSelectListing = (id) => {
    setSelectedId(id);
    resetSelection();
  };

  const handleDayClick = (iso, info) => {
    setQuoteError(null);
    setSubmitError(null);
    if (!checkIn || (checkIn && checkOut)) {
      if (!info?.available) return;
      setCheckIn(iso);
      setCheckOut(null);
      setQuote(null);
      return;
    }
    if (iso > checkIn) {
      setCheckOut(iso);
    } else {
      if (!info?.available) return;
      setCheckIn(iso);
      setCheckOut(null);
      setQuote(null);
    }
  };

  useEffect(() => {
    if (!listing || !checkIn || !checkOut) return;
    const fetchQuote = async () => {
      setQuoting(true);
      setQuote(null);
      setQuoteError(null);
      try {
        const res = await fetch(`${API}/api/bookings/quote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ listing_id: listing.id, check_in: checkIn, check_out: checkOut }),
        });
        const data = await res.json();
        if (!res.ok) {
          setQuoteError(data.detail || "These dates are not available.");
          return;
        }
        setQuote(data);
      } catch {
        setQuoteError("Could not calculate the price. Please try again.");
      } finally {
        setQuoting(false);
      }
    };
    fetchQuote();
  }, [listing, checkIn, checkOut]);

  const formValid = form.first_name.trim() && form.last_name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const handleAddToCart = () => {
    if (!quote || !listing) return;
    addStay({
      listing_id: listing.id,
      listing_title: listing.title || listing.nickname,
      check_in: checkIn,
      check_out: checkOut,
      nights: quote.nights,
      guests_count: Number(form.guests_count),
      amount: quote.total,
    });
    selectedAddons.forEach((a) => addExperience(a));
    setAddonIds([]);
    toast.success("Added to cart", { description: "Open the cart in the top menu to check out." });
  };

  const handleSubmit = async () => {
    if (!quote || !formValid) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API}/api/bookings/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listing_id: listing.id,
          check_in: checkIn,
          check_out: checkOut,
          guests_count: Number(form.guests_count),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          origin_url: window.location.origin,
          addon_ids: addonIds,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.detail || "Could not start checkout. Please try again.");
        setSubmitting(false);
        return;
      }
      window.location.href = data.url;
    } catch {
      setSubmitError("Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  if (listingsLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="booking-flow-loading">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  if (listingsError || !listing) {
    return (
      <p data-testid="booking-flow-error" className="font-body text-sm text-muted-foreground">
        {listingsError || "Online booking is temporarily unavailable. Please contact us to book."}
      </p>
    );
  }

  return (
    <div data-testid="booking-flow">
      {listings.length > 1 && (
        <ListingPicker listings={listings} selected={listing} onSelect={handleSelectListing} />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
        <LiveAvailability
          listing={listing}
          selection={{ checkIn, checkOut }}
          onDayClick={handleDayClick}
        />
        <StayPanel
          listing={listing}
          checkIn={checkIn}
          checkOut={checkOut}
          quoting={quoting}
          quoteError={quoteError}
          quote={quote}
          selectedAddons={selectedAddons}
          grandTotal={grandTotal}
          experiences={experiences}
          addonIds={addonIds}
          toggleAddon={toggleAddon}
          form={form}
          setForm={setForm}
          submitError={submitError}
          submitting={submitting}
          formValid={formValid}
          onSubmit={handleSubmit}
          onAddToCart={handleAddToCart}
        />
      </div>
    </div>
  );
};
